/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useRef, useState, Fragment } from 'react';
import Tooltip from './tooltip';
import { sdgsIcons } from './config';
import { ChartStyle } from './style';

const Chart = ({
  data = [],
  t = (t) => t,
  withBody = true,
  customBody,
  className,
  renderTooltip,
  doConstraints = true,
  showSources = true,
  isEmpty = false,
  background,
  changeOpacityOnHover = true,
  yOffset = 130,
  xOffset = 230,
  isPie = false,
  mouseXOffset = 30,
  mouseYOffset = 50,
}) => {
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const svgRef = useRef();
  const containerRef = useRef();
  const [mouseY, setMouseY] = useState(0);
  const [mouseX, setMouseX] = useState(0);
  const [mouseLocked, setMouseLocked] = useState(false);

  const getCoordinatesForPercent = useCallback((percent) => [
    Math.cos(2 * Math.PI * percent),
    Math.sin(2 * Math.PI * percent),
  ], []);

  const onMouseLeaveHandler = useCallback((e, hG, id) => {
    if (mouseLocked) {
      return;
    }

    let x = e.clientX - svgRef.current.getBoundingClientRect().left + mouseXOffset;
    let y = e.clientY - svgRef.current.getBoundingClientRect().top + mouseYOffset;

    if (doConstraints && containerRef.current.clientWidth - x < xOffset) {
      x = containerRef.current.clientWidth - xOffset;
    }

    if (doConstraints && containerRef.current.clientHeight - y < yOffset) {
      y = 0;
      x = containerRef.current.clientWidth / 2 - 100;
    }

    setMouseY(y);
    setMouseX(x);

    if (hoveredGroup === null) {
      setHoveredGroup({ ...hG, id });
    }
  }, [svgRef, containerRef, hoveredGroup, mouseLocked]);

  const dataToShow = useMemo(() => {
    let cumulativePercent = 0;

    return data.map((d, i) => {
      const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
      cumulativePercent += d.percent - 0;
      const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
      const [borderStartX, borderStartY] = getCoordinatesForPercent(cumulativePercent);
      cumulativePercent += 0;
      const [borderEndX, borderEndY] = getCoordinatesForPercent(cumulativePercent);
      const largeArcFlag = d.percent > .5 ? 1 : 0;

      const pathData = [
        `M ${startX} ${startY}`,
        `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        `L 0 0`,
      ].join(' ');

      const borderPathData = [
        `M ${borderStartX} ${borderStartY}`,
        `A 1 1 0 0 1 ${borderEndX} ${borderEndY}`,
        `L 0 0`,
      ].join(' ');

      return (
        <Fragment key={i.toString()}>
          <path
            d={pathData}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Tooltip on top"
            fill={d.color}
            style={{
              opacity: changeOpacityOnHover
                ? hoveredGroup === null ? 1 : hoveredGroup.id === i ? 1 : 0.4
                : 1,
            }}
            onMouseEnter={() => {
              if (mouseLocked) {
                return;
              }

              setHoveredGroup({ ...d, id: i })
            }}
            onMouseLeave={() => {
              if (mouseLocked) {
                return;
              }

              setHoveredGroup(null)
            }}
            onMouseMove={(e) => onMouseLeaveHandler(e, d, i)}
            onClick={() => setMouseLocked((prev) => !prev)}
          />
          <path
            d={borderPathData}
            fill="white"
          />
        </Fragment>
      );
    });
  }, [data, hoveredGroup, getCoordinatesForPercent, mouseLocked]);

  const tooltip = useMemo(() => hoveredGroup !== null ?
    renderTooltip ?
      renderTooltip({ mouseX, mouseY, item: hoveredGroup }) : (
        <Tooltip
          t={t}
          mouseX={mouseX}
          mouseY={mouseY}
          setHoveredGroup={setHoveredGroup}
          hoveredGroup={hoveredGroup}
          label={hoveredGroup.label}
          src={sdgsIcons[hoveredGroup.title]?.src}
          showSources={showSources}
        />
      ) : null, [hoveredGroup, mouseX, mouseY]);

  return (
    <ChartStyle
      ref={containerRef}
      className={className}
      onMouseLeave={() => {
        if (mouseLocked) {
          setHoveredGroup(null)
          setMouseLocked(false);
        }
      }}
    >
      <svg
        viewBox="-1 -1 2 2"
        ref={svgRef}
        style={{ background }}
        {...(isEmpty ? {
          onMouseEnter: () => {
            if (mouseLocked) {
              return;
            }

            setHoveredGroup({ id: 0 })
          },
          onMouseLeave: () => {
            if (mouseLocked) {
              return;
            }

            setHoveredGroup(null)
          },
          onMouseMove: (e) => onMouseLeaveHandler(e, {}, 0),
        } : {})}
      >
        {dataToShow}
      </svg>
      {tooltip}
      {isPie ? null : (
        <div className="info">
          {withBody ? customBody || (
            <>
              <p className="text-blue">
                {data.length}
              </p>
              <p> {t('Dashboard::SDGs')}</p>
            </>
          ) : null}
        </div>
      )}
    </ChartStyle>
  );
};

export default Chart;
